import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CloudRain, ArrowLeft, Users, FileText, CreditCard, MagnifyingGlass, CheckCircle, XCircle, Warning, CurrencyDollar } from '@phosphor-icons/react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = window.location.origin;

const AdminDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentQueries, setRecentQueries] = useState([]);
  const [systemStatus, setSystemStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchDashboardData();
      fetchSystemStatus();
    }
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/admin/dashboard`,
        { withCredentials: true }
      );
      setStats(data.stats);
      setRecentQueries(data.recent_queries);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchSystemStatus = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/system/status`);
      setSystemStatus(data);
    } catch (error) {
      console.error('Failed to load system status');
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-950 mb-4" style={{ fontFamily: 'Chivo' }}>Admin Access Required</h2>
          <Link to="/dashboard" className="text-blue-600 hover:text-blue-700">Return to Dashboard</Link>
        </div>
      </div>
    );
  }

  const StatusBadge = ({ status }) => {
    const isLive = status === 'connected' || status === 'live';
    const isLimited = status === 'limited' || status === 'mocked' || status === 'MOCKED';
    
    if (isLive) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-600 border border-emerald-200 text-xs font-mono uppercase">
          <CheckCircle size={14} weight="bold" />
          LIVE
        </span>
      );
    } else if (isLimited) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-50 text-amber-600 border border-amber-200 text-xs font-mono uppercase">
          <Warning size={14} weight="bold" />
          LIMITED
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-600 border border-slate-200 text-xs font-mono uppercase">
          <XCircle size={14} weight="bold" />
          NOT CONFIGURED
        </span>
      );
    }
  };

  return (
    <div className="min-h-screen bg-white mobile-nav-padding">
      <header className="border-b border-slate-200 bg-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center gap-2">
              <CloudRain size={28} weight="bold" className="text-blue-600" />
              <span className="text-xl sm:text-2xl font-bold tracking-tight text-slate-950" style={{ fontFamily: 'Chivo' }}>Admin</span>
            </Link>
            <Link
              to="/dashboard"
              className="text-sm text-slate-600 hover:text-slate-950 transition-colors flex items-center gap-1.5"
            >
              <ArrowLeft size={16} weight="bold" />
              <span className="hidden sm:inline">Back to Dashboard</span>
              <span className="sm:hidden">Back</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-16">
        {/* Data Source Status */}
        {systemStatus && (
          <div className="mb-12" data-testid="data-source-status-panel">
            {(() => {
              const weatherLive = systemStatus.services?.weather?.visual_crossing?.configured;
              const geocodingLive = systemStatus.services?.geocoding?.configured;
              const allLive = weatherLive && geocodingLive;

              return allLive ? (
                <div className="p-6 bg-emerald-50 border border-emerald-200">
                  <div className="flex items-start gap-3">
                    <CheckCircle size={24} weight="bold" className="text-emerald-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h2 className="text-base font-semibold text-emerald-900" style={{ fontFamily: 'Chivo' }} data-testid="status-live-title">Live Data Connected</h2>
                      <p className="text-sm text-emerald-700 mt-1">Weather data is being pulled from verified external providers.</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-6 bg-amber-50 border border-amber-200">
                  <div className="flex items-start gap-3">
                    <Warning size={24} weight="bold" className="text-amber-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h2 className="text-base font-semibold text-amber-900" style={{ fontFamily: 'Chivo' }} data-testid="status-limited-title">Limited Mode</h2>
                      <p className="text-sm text-amber-700 mt-1">Some data may be simulated. Add API keys to enable full functionality.</p>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Detailed Service Status */}
            <div className="mt-4 p-6 bg-slate-50 border border-slate-200">
              <h3 className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500 mb-4">Service Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-slate-500 mb-2">Weather API</p>
                  <StatusBadge status={systemStatus.services?.weather?.visual_crossing?.status} />
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-2">Geocoding API</p>
                  <StatusBadge status={systemStatus.services?.geocoding?.status} />
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-2">Email Service</p>
                  <StatusBadge status={systemStatus.services?.email?.status} />
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-2">Payment Gateway</p>
                  <StatusBadge status={systemStatus.services?.stripe?.mode} />
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-slate-200">
                <p className="text-xs text-slate-500">
                  Environment: <span className="font-mono font-semibold text-slate-950">{systemStatus.environment?.toUpperCase()}</span>
                </p>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="mt-4 text-sm text-slate-600 font-mono">Loading...</p>
          </div>
        ) : (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              <div className="bg-white p-6 border border-slate-200">
                <div className="flex items-center gap-3 mb-4">
                  <Users size={24} weight="bold" className="text-blue-600" />
                  <p className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500">Total Users</p>
                </div>
                <p className="text-3xl font-mono font-bold text-slate-950">{stats?.total_users || 0}</p>
                <div className="mt-3 pt-3 border-t border-slate-200 text-xs text-slate-600">
                  <p>Free: {stats?.free_users || 0}</p>
                  <p>Pro: {stats?.pro_users || 0}</p>
                </div>
              </div>

              <div className="bg-white p-6 border border-slate-200">
                <div className="flex items-center gap-3 mb-4">
                  <MagnifyingGlass size={24} weight="bold" className="text-blue-600" />
                  <p className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500">Total Queries</p>
                </div>
                <p className="text-3xl font-mono font-bold text-slate-950">{stats?.total_queries || 0}</p>
              </div>

              <div className="bg-white p-6 border border-slate-200">
                <div className="flex items-center gap-3 mb-4">
                  <FileText size={24} weight="bold" className="text-blue-600" />
                  <p className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500">Reports Generated</p>
                </div>
                <p className="text-3xl font-mono font-bold text-slate-950">{stats?.total_reports || 0}</p>
              </div>

              <div className="bg-white p-6 border border-slate-200">
                <div className="flex items-center gap-3 mb-4">
                  <CreditCard size={24} weight="bold" className="text-blue-600" />
                  <p className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500">Payments</p>
                </div>
                <p className="text-3xl font-mono font-bold text-slate-950">{stats?.successful_payments || 0}</p>
                <p className="text-xs text-slate-500 mt-2">of {stats?.total_payments || 0} total</p>
              </div>

              <div className="bg-white p-6 border border-slate-200">
                <div className="flex items-center gap-3 mb-4">
                  <CurrencyDollar size={24} weight="bold" className="text-emerald-600" />
                  <p className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500">Total Revenue</p>
                </div>
                <p className="text-3xl font-mono font-bold text-slate-950" data-testid="total-revenue">
                  ${(stats?.total_revenue || 0).toFixed(2)}
                </p>
                <p className="text-xs text-slate-500 mt-2">From {stats?.successful_payments || 0} paid reports at $9.99 each</p>
                {(stats?.total_revenue || 0) === 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-200">
                    <p className="text-xs font-medium text-slate-600">System Status: Pre-Revenue (Testing Phase)</p>
                    <p className="text-xs text-slate-500 mt-0.5">Payments will begin once live data is enabled.</p>
                  </div>
                )}
              </div>

              <div className="bg-white p-6 border border-slate-200" data-testid="live-data-status-card">
                <div className="flex items-center gap-3 mb-4">
                  {(() => {
                    const allLive = systemStatus?.services?.weather?.visual_crossing?.configured && systemStatus?.services?.geocoding?.configured;
                    return allLive
                      ? <CheckCircle size={24} weight="bold" className="text-emerald-600" />
                      : <Warning size={24} weight="bold" className="text-amber-500" />;
                  })()}
                  <p className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500">Live Data Status</p>
                </div>
                {(() => {
                  const allLive = systemStatus?.services?.weather?.visual_crossing?.configured && systemStatus?.services?.geocoding?.configured;
                  return allLive ? (
                    <p className="text-sm font-semibold text-emerald-700" data-testid="live-data-status-text">Live Data Connected</p>
                  ) : (
                    <p className="text-sm font-semibold text-amber-700" data-testid="live-data-status-text">Limited Mode</p>
                  );
                })()}
              </div>
            </div>

            {/* Recent Queries */}
            <div>
              <h2 className="text-2xl font-bold tracking-tight text-slate-950 mb-6" style={{ fontFamily: 'Chivo' }}>Recent Queries</h2>
              {recentQueries.length === 0 ? (
                <div className="bg-slate-50 border border-slate-200 p-12 text-center">
                  <p className="text-sm text-slate-600">No queries yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentQueries.map((query, index) => (
                    <div key={index} className="bg-white border border-slate-200 p-4">
                      <div className="grid grid-cols-1 md:grid-cols-5 gap-3 text-xs">
                        <div>
                          <p className="text-slate-500 mb-1">Location</p>
                          <p className="font-medium text-slate-950">{query.location}</p>
                        </div>
                        <div>
                          <p className="text-slate-500 mb-1">Date</p>
                          <p className="font-mono text-slate-950">{query.date}</p>
                        </div>
                        <div>
                          <p className="text-slate-500 mb-1">Time</p>
                          <p className="font-mono text-slate-950">{query.time}</p>
                        </div>
                        <div>
                          <p className="text-slate-500 mb-1">User ID</p>
                          <p className="font-mono text-slate-950">{query.user_id?.slice(0, 8)}...</p>
                        </div>
                        <div>
                          <p className="text-slate-500 mb-1">Created</p>
                          <p className="font-mono text-slate-950">{new Date(query.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
